import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Box, Button, Card, CardContent, Menu, MenuItem, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { eachDayOfInterval, endOfWeek, format, startOfWeek } from 'date-fns'
import { useMemo, useState } from 'react'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { useAppointmentsInRange } from '../../api/analytics'
import { STATUS_COLORS } from '../../ReportsPage/constants.ts'
import {
  exportData,
  formatDateTime,
  transformSubType,
  transformType,
} from '../../utils/export.utils'
import { BarChartLoading } from '../Loading'

import { getAppropriateTime } from './utils.ts'

interface ChartData {
  date: string
  count: number
  dispatched?: number
  completed?: number
  cancelled?: number
  new?: number
}

interface AppointmentsBarChartProps {
  timeColumn?: string
  title?: string
  status?: string
}

export const AppointmentsBarChart: React.FC<AppointmentsBarChartProps> = ({
  timeColumn = 'scheduleStartTime',
  title = 'Dispatched Appointments by Schedule Date',
  status = 'dispatched',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // Default to current week (Monday to Sunday)
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const today = new Date()
    return startOfWeek(today, { weekStartsOn: 1 }) // Monday
  })
  const [toDate, setToDate] = useState<Date | null>(() => {
    const today = new Date()
    return endOfWeek(today, { weekStartsOn: 1 }) // Sunday
  })

  // Format dates for API (RFC3339)
  const fromDateStr = fromDate ? fromDate.toISOString() : ''
  const toDateStr = toDate ? toDate.toISOString() : ''

  // Fetch data with date filters, using timeColumn and status filter
  const { data: appointmentsData, isLoading } = useAppointmentsInRange(
    fromDateStr,
    toDateStr,
    timeColumn,
    status,
    'live'
  )
  const appointments = appointmentsData?.data

  const chartData = useMemo<ChartData[]>(() => {
    if (!fromDate || !toDate) {
      return []
    }

    // Group appointments by date and status based on timeColumn
    const appointmentsByDateAndStatus: Record<
      string,
      { dispatched: number; completed: number; cancelled: number; new: number; total: number }
    > = {}

    if (appointments) {
      appointments.forEach(appointment => {
        const timeValue = getAppropriateTime(appointment, timeColumn)
        if (timeValue) {
          const timeDate = new Date(timeValue)
          const date = format(timeDate, 'yyyy-MM-dd')

          if (!appointmentsByDateAndStatus[date]) {
            appointmentsByDateAndStatus[date] = {
              dispatched: 0,
              completed: 0,
              cancelled: 0,
              new: 0,
              total: 0,
            }
          }

          // Increment count for the specific status
          const appointmentStatus = appointment.status.toLowerCase()
          if (appointmentStatus === 'dispatched') {
            appointmentsByDateAndStatus[date].dispatched++
          } else if (appointmentStatus === 'completed') {
            appointmentsByDateAndStatus[date].completed++
          } else if (appointmentStatus === 'cancelled') {
            appointmentsByDateAndStatus[date].cancelled++
          } else if (appointmentStatus === 'new') {
            appointmentsByDateAndStatus[date].new++
          }
          appointmentsByDateAndStatus[date].total++
        }
      })
    }

    // Generate all dates in the range
    const dateRange = eachDayOfInterval({
      start: fromDate,
      end: toDate,
    })

    // Create chart data with all dates (including those with 0 appointments)
    return dateRange.map((date): ChartData => {
      const dateKey = format(date, 'yyyy-MM-dd')
      const statusCounts = appointmentsByDateAndStatus[dateKey] || {
        dispatched: 0,
        completed: 0,
        cancelled: 0,
        new: 0,
        total: 0,
      }

      return {
        date: format(date, 'EEE MMM dd'),
        count: statusCounts.total,
        dispatched: statusCounts.dispatched,
        completed: statusCounts.completed,
        cancelled: statusCounts.cancelled,
        new: statusCounts.new,
      }
    })
  }, [appointments, fromDate, toDate, timeColumn])

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (format: 'csv' | 'xlsx') => {
    if (!appointments) return

    // Transform OnSiteAppointment data for export using the same format as transformAppointments
    const transformedData = appointments.map(appointment => ({
      // Customer Information
      'Customer Name': appointment.customerName || '-',
      'Customer Address': appointment.customerAddress || '-',
      'Customer Phone': appointment.customerPhone || '-',
      'Customer Email': appointment.customerEmail || '-',
      // Appointment Details
      UUID: appointment.uuid || '-',
      'Building UUID': appointment.buildingUUID || '-',
      'Customer UUID': appointment.customerAccountUUID || '-',
      'Scout UUID': appointment.scoutUUID || '-',
      Status: appointment.status || '-',
      'Residential Units': appointment.residentialUnits || '-',
      // Dates
      'Creation Date': appointment.createdAt ? formatDateTime(appointment.createdAt) : '-',
      'Scheduled Date': appointment.scheduleStartTime
        ? formatDateTime(appointment.scheduleStartTime)
        : '-',
      'Status Changed': appointment.statusChangedAt
        ? formatDateTime(appointment.statusChangedAt)
        : '-',
      Type: transformType(appointment),
      SubType: transformSubType(appointment),
    }))

    // Export using the utility function
    exportData(transformedData, {
      filename: 'appointments',
      sheetName: 'Appointments',
      format,
    })

    handleExportClose()
  }

  if (isLoading) return <BarChartLoading title={title} />

  const maxValue = Math.max(...chartData.map(d => d.count), 10)
  const yAxisMax = Math.ceil(maxValue / 5) * 5 // Round up to nearest 5

  // Calculate dynamic width based on number of days (minimum 60px per bar)
  const barWidth = 60
  const chartWidth = Math.max(800, chartData.length * barWidth)
  const chartHeight = 320

  return (
    <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#2D3748',
                mb: 2,
              }}
            >
              {title}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={newValue => setFromDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 180 },
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={newValue => setToDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 180 },
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {chartData.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: chartHeight,
            position: 'relative',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <Box sx={{ width: chartWidth, height: chartHeight, minWidth: 800 }}>
            <ResponsiveContainer width="100%" height="135%">
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 100,
                }}
              >
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#718096', fontSize: 11 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis
                  tick={{ fill: '#718096' }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  domain={[0, yAxisMax]}
                  ticks={Array.from({ length: 5 }, (_, i) => Math.round((yAxisMax / 4) * i))}
                  label={{
                    value: '',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#718096' },
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#2D3748', fontWeight: 600 }}
                />
                <Bar
                  dataKey="dispatched"
                  stackId="status"
                  fill={STATUS_COLORS.dispatched}
                  radius={[0, 0, 0, 0]}
                  maxBarSize={60}
                  name="Dispatched"
                />
                <Bar
                  dataKey="completed"
                  stackId="status"
                  fill={STATUS_COLORS.completed}
                  radius={[0, 0, 0, 0]}
                  maxBarSize={60}
                  name="Completed"
                />
                <Bar
                  dataKey="cancelled"
                  stackId="status"
                  fill={STATUS_COLORS.cancelled}
                  radius={[0, 0, 0, 0]}
                  maxBarSize={60}
                  name="Cancelled"
                />
                <Bar
                  dataKey="new"
                  stackId="status"
                  fill={STATUS_COLORS.new}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={60}
                  name="New"
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default AppointmentsBarChart
